import React from "react";
// Shared UI primitives + icons

const { useState, useEffect, useRef, useMemo } = React;

const I = (d, extra) => (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...p}>
    {d.split("|").map((seg, i) => <path key={i} d={seg}/>)}{extra}
  </svg>
);
const Icon = {
  home: I("M3 11l9-7 9 7v9a2 2 0 0 1-2 2h-4v-6h-6v6H5a2 2 0 0 1-2-2v-9z"),
  horse: I("M4 21l1.5-7L3 11l3-2 2-3h4l3 2 4 1 2 3-2 2-1 2v5|M8 8l-1-3", <circle cx="16" cy="9" r=".5" fill="currentColor"/>),
  cam: I("M16 10l6-3v10l-6-3z", <><rect x="2" y="7" width="14" height="10" rx="2"/><circle cx="9" cy="12" r="2"/></>),
  clipboard: I("M9 4h6v3H9z|M8 11h8M8 15h5", <rect x="5" y="4" width="14" height="17" rx="2"/>),
  tv: I("M8 21h8M9 18v3M15 18v3", <rect x="3" y="5" width="18" height="13" rx="2"/>),
  dining: I("M7 3v8M5 3v5a2 2 0 0 0 4 0V3|M7 11v10|M17 3c-2 0-3 2-3 5s1 4 3 4v9|M17 3v18"),
  shield: I("M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z|M9 12l2 2 4-4"),
  users: I("M16 20v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2|M22 20v-2a4 4 0 0 0-3-3.87M16 4.13A4 4 0 0 1 16 11.6", <circle cx="9" cy="7" r="3"/>),
  search: I("m21 21-5-5", <circle cx="11" cy="11" r="7"/>),
  arrow: I("M5 12h14M13 6l6 6-6 6"),
  back: I("M19 12H5M11 6l-6 6 6 6"),
  plus: I("M12 5v14M5 12h14"),
  power: I("M12 3v9M8 6a8 8 0 1 0 8 0"),
  vol: I("M3 10v4h4l5 4V6L7 10H3z|M16 8a5 5 0 0 1 0 8M19 5a9 9 0 0 1 0 14"),
  mute: I("M3 10v4h4l5 4V6L7 10H3zM16 9l5 6M21 9l-5 6"),
  camera: I("M4 7h3l2-3h6l2 3h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2z", <circle cx="12" cy="13" r="4"/>),
  eye: I("M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7z", <circle cx="12" cy="12" r="3"/>),
  play: (p) => <svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M7 4v16l13-8z"/></svg>,
  xray: I("M7 8l10 8M17 8 7 16M7 12h10M12 7v10", <rect x="3" y="3" width="18" height="18" rx="2"/>),
  star: (p) => <svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="m12 2 3 7h7l-5.5 4.5L18 21l-6-4-6 4 1.5-7.5L2 9h7z"/></svg>,
  expand: I("M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"),
  check: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}><path d="m5 12 5 5L20 7"/></svg>,
  filter: I("M3 5h18l-7 9v6l-4-2v-4z"),
  burger: I("M3 6h18M3 12h18M3 18h18"),
  chev: I("M6 9l6 6 6-6"),
  close: I("M6 6l12 12M18 6 6 18"),
  grid: I("M3 3h8v8H3zM13 3h8v8h-8zM3 13h8v8H3zM13 13h8v8h-8z"),
  clock: I("M12 7v5l3 3", <circle cx="12" cy="12" r="9"/>),
  mail: I("M3 7l9 6 9-6", <rect x="3" y="5" width="18" height="14" rx="2"/>),
  trash: I("M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v6M14 11v6"),
  link: I("M10 14a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1.5 1.5|M14 10a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7L12.5 18.5"),
};

// ============== Module gate ==============
// A section only renders when something is actually behind it. Modules with no
// provider and no fixture say so plainly, and modules the public catalogue lists
// as disabled say that too, so a fixture preview is never mistaken for a
// shipped, catalogue-enabled section.
const MODULE_LABELS = {
  "foundation.sale_lot_mirror": "Sale & lot mirror",
  "foundation.vendor_registry": "Vendor registry",
  "vendor.stats": "Vendor stats",
  "stable.camera_media": "Stable cameras",
  "live.parade": "Parade streaming",
  "inspections": "Inspections",
  "tv": "TV control",
  "vendor.admin": "Vendor admin",
};
const moduleLabel = (name) => MODULE_LABELS[name] || name;

function MockNote({ module }) {
  if (!window.VD.isMocked(module)) return null;
  return <span className="mocknote">Mock data</span>;
}

function ModuleGate({ modules, children }) {
  const names = Array.isArray(modules) ? modules : [modules];
  const missing = names.filter(n => window.VD.source(n) === "unavailable");
  if (missing.length === 0) return children;
  return (
    <div className="fade-in modulegate">
      <div className="eyebrow">No data source</div>
      <div className="display" style={{ marginTop: 6 }}>{missing.map(moduleLabel).join(" · ")}</div>
      <p>
        {missing.length === 1 ? "This module has" : "These modules have"} no provider in this
        environment and no mock fixture enabled. Turn its fixture on with
        {" "}<span className="mono">VENDORDASH_MOCK_MODULES</span> to walk the design, or wait for
        the provider to land.
      </p>
      <p className="caption" style={{ marginTop: 10 }}>
        {missing.map(n => `${n} · catalogue ${window.VD.moduleInfo(n).catalogue}`).join(" — ")}
      </p>
    </div>
  );
}

// ============== Sparkline ==============
function Sparkline({ data, color = "var(--inglis-blue)", height = 36, area = true }) {
  const pts = useMemo(() => {
    if (!data || data.length < 2) return [];
    const max = Math.max(...data, 1), min = Math.min(...data, 0);
    const range = max - min || 1;
    return data.map((v, i) => [(i / (data.length - 1)) * 100, 100 - ((v - min) / range) * 100]);
  }, [data]);
  if (pts.length === 0) return <svg className="spark" style={{ height }}/>;
  const d = pts.map((p, i) => `${i ? "L" : "M"}${p[0]},${p[1]}`).join(" ");
  return (
    <svg className="spark" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ height }}>
      {area && <><defs><linearGradient id="sg" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stopColor={color} stopOpacity="0.35"/><stop offset="100%" stopColor={color} stopOpacity="0"/>
      </linearGradient></defs><path d={d + " L100,100 L0,100 Z"} fill="url(#sg)"/></>}
      <path d={d} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke"/>
    </svg>
  );
}

// ============== Box snapshot ==============
// Live-first (Option D): the still is a ~5-minute thumbnail; tapping through
// opens an on-demand cropped live stream of the box.
function useSnapAge(box) {
  const cycle = window.DATA.SNAPSHOT_INTERVAL || 5;
  const base = (box * 97) % cycle; // minutes into the refresh cycle
  const [tick, setTick] = useState(0);
  useEffect(() => { const id = setInterval(() => setTick(t => t + 1), 60000); return () => clearInterval(id); }, []);
  return (base + tick) % cycle + 1;
}
function BoxSnap({ box, style, meta = true, lotLabel, live = false }) {
  const age = useSnapAge(box || 0);
  // deterministic crop per box — each camera frames eight boxes, two rows of four
  const x = box ? (box * 37) % 55 : 25;
  const y = box ? 15 + (box * 53) % 45 : 35;
  const cam = box ? window.DATA.camForBox(box) : null;
  const src = box ? window.DATA.camImgForBox(box) : "";
  return (
    <div className="boxsnap" style={style}>
      {box && src ? (
        <img src={src} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: `${x}% ${y}%`, display: "block" }}/>
      ) : (
        <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", background: "var(--inglis-blue-tint)" }}>
          <span style={{ fontSize: 12, color: "var(--inglis-blue)", fontWeight: 700 }}>
            {box ? "No camera frame" : "No box assigned"}
          </span>
        </div>
      )}
      {box && src && live && <span className="livechip"><span className="pulse"/>LIVE</span>}
      {box && src && meta && (
        <div className="snapmeta">
          <span>K{box} · {cam}{lotLabel ? ` · ${lotLabel}` : ""}</span>
          <span>{live ? "Streaming" : `Updated ${age} min ago`}</span>
        </div>
      )}
    </div>
  );
}

// ============== Card / Tag / Stat / Clock ==============
function Card({ title, subtitle, right, children, className = "", style }) {
  return (
    <div className={`card ${className}`} style={style}>
      {(title || right) && (
        <div className="card-title">
          <div><h3>{title}</h3>{subtitle && <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{subtitle}</div>}</div>
          {right}
        </div>
      )}
      {children}
    </div>
  );
}
function Tag({ children, variant = "" }) { return <span className={`tag ${variant}`}>{children}</span>; }
function Stat({ label, value, delta, deltaDir = "up" }) {
  return (
    <div className="stat">
      <div className="v">{value}{delta != null && <span className={`delta ${deltaDir === "down" ? "down" : ""}`}>{deltaDir === "down" ? "↘" : "↗"} {delta}</span>}</div>
      <div className="l">{label}</div>
    </div>
  );
}
function LiveClock() {
  const [t, setT] = useState(window.DATA.now());
  useEffect(() => { const i = setInterval(() => setT(window.DATA.now()), 1000); return () => clearInterval(i); }, []);
  const h = t.getHours(), m = String(t.getMinutes()).padStart(2, "0");
  return (
    <div className="clock">
      <span>{String(((h + 11) % 12) + 1).padStart(2, "0")}:{m}</span>
      <span className="meridian">{h >= 12 ? "PM" : "AM"}</span>
    </div>
  );
}
function demoTimeHM() {
  const t = window.DATA.now();
  return `${String(t.getHours()).padStart(2, "0")}:${String(t.getMinutes()).padStart(2, "0")}`;
}

// Shared box-assignment state (localStorage-backed, cross-view)
function useBoxAssign() {
  const [map, setMap] = useState(() => window.DATA.loadAssign());
  useEffect(() => {
    const fn = () => setMap(window.DATA.loadAssign());
    window.addEventListener("vd-assign", fn);
    return () => window.removeEventListener("vd-assign", fn);
  }, []);
  const assign = (next) => { window.DATA.saveAssign(next); window.dispatchEvent(new Event("vd-assign")); };
  return [map, assign];
}

export { Icon, Sparkline, BoxSnap, Card, Tag, Stat, LiveClock, demoTimeHM, useBoxAssign, ModuleGate, MockNote, moduleLabel };
